import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import { useAppDispatch } from '../../../../redux/hooks';
import useCapabilityRelease from '../../../../hooks/useCapabilityRelease';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { getLLAuthHeaders } from '../../utils/llAuthHeaders';
import { IOrgData, orgSettingsActions } from '../../../../redux/slices/orgDetails/orgDetailsSlice';
import { setOrgMemberAttributes } from '../../../../redux/slices/user-profile/userProfieSlice';
import {
  buildGroupPutPayload,
  detectAttemptedGrpDeselection,
  isMongoObjectIdString,
  mergeGrpSelection,
  normalizeOrgCatalog,
  parseGroupMemberAttributesResponse,
  unwrapEnvelope,
  type MemberAttributeGroupState
} from '../../utils/memberAttributes';
import { ATTR_AUTHORED_KEY } from '../../components/useMemberAttributesPrompt';
import { ManagerPicker, useTeamManagers } from './TeamManagersSection';
import TeamRolesSection from './TeamRolesSection';
import { MOBILE_SX } from '../../../../mobile/mobileApp';
import { ValuesInput } from './orgSettingsInputs';
import {
  NAME_COLUMN,
  addRowButtonSx,
  cardHeaderSx,
  cardSx,
  cardTitleSx,
  columnHeadingSx,
  helperTextSx,
  inputSx,
  sectionTitleSx
} from './orgSettingsStyles';

// Audience Attributes — the org's member attribute lists, edited in place on
// Organization Settings (they used to sit in a "Manage Attributes" modal).
//
// - Department is always first and must have values: it is the org's Teams
//   list (the one marked isTeamsList, else one named "Teams", as the BE reads
//   it), which Capabilities uses as the teams and Capability Gaps reports by.
//   Each department is a row with its managers, and every department needs
//   one. An org without a Teams list sees Department empty; saving it creates
//   the list and marks it as the Teams list.
// - Roles are each department's roles from Capabilities, locked here.
// - Every other list is its own section, free to edit, and new lists can be
//   created.
//
// Lists save together on Save Changes; managers save as they change (a new
// department gets its managers once it is saved).

const DEPARTMENT = 'Department';

interface Props {
  data: IOrgData;
  grpId: string;
}

const isObjectId = (val: string) => /^[a-f0-9]{24}$/i.test(val);
const CORRUPTED_NAMES = new Set(['grpmemberattributes', 'orgmemberattributes', 'memberattributes']);
const isCorruptedName = (name: string) =>
  isObjectId(name) || CORRUPTED_NAMES.has(name.toLowerCase().replace(/[_\- ]/g, ''));
const isDepartmentName = (name: string) => name.trim().toLowerCase() === DEPARTMENT.toLowerCase();

// The Teams list as the BE resolves it (src/service/teamsList.ts): the list
// marked isTeamsList, else a list named "Teams".
const resolveTeamsListId = (memberAttributes: unknown): string | null => {
  const lists: any[] = Array.isArray(memberAttributes) ? memberAttributes : [];
  const list =
    lists.find((l) => l?.isTeamsList === true) ||
    lists.find(
      (l) =>
        String(l?.name || '')
          .trim()
          .toLowerCase() === 'teams'
    );
  return list?._id ? String(list._id) : null;
};

// Department straight from the GET, every value included. The catalog view
// hides some value names (isHiddenMemberAttributeName), but each department
// needs its row and its managers.
const rawDepartmentList = (
  payload: any,
  teamsListId: string | null,
  grpIds: string[]
): MemberAttributeGroupState | null => {
  const raw: any[] = Array.isArray(payload?.orgMemberAttributes) ? payload.orgMemberAttributes : [];
  const list = raw.find((l) =>
    teamsListId ? String(l?._id ?? '') === teamsListId : isDepartmentName(String(l?.name ?? ''))
  );
  if (!list?._id) return null;
  const selected = new Set(grpIds.map(String));
  const values = (Array.isArray(list.values) ? list.values : [])
    .filter((v: any) => v?._id && v?.value && !isObjectId(String(v.value)))
    .map((v: any) => ({ _id: String(v._id), value: String(v.value), selected: selected.has(String(v._id)) }));
  return { _id: String(list._id), name: String(list.name ?? DEPARTMENT), values };
};

// Fallback parser for older GET shapes that parseGroupMemberAttributesResponse
// doesn't read.
const normalizeAttributeLists = (payload: any, grpSelectedIds: string[]): MemberAttributeGroupState[] => {
  const d = payload?.data ?? payload ?? {};
  const selectedSet = new Set((grpSelectedIds || []).map(String));
  const orgAttributes = Array.isArray(d?.orgMemberAttributes) ? d.orgMemberAttributes : [];
  const validOrgAttrs = orgAttributes.filter((g: any) => g && typeof g === 'object' && typeof g !== 'string');
  if (validOrgAttrs.length) {
    return mergeGrpSelection(normalizeOrgCatalog(validOrgAttrs, undefined), grpSelectedIds);
  }
  const rawAttributes =
    d?.attributes ??
    (Array.isArray(d?.memberAttributes) && d.memberAttributes.length > 0 && typeof d.memberAttributes[0] === 'object'
      ? d.memberAttributes
      : null) ??
    [];
  const toValue = (option: any) => {
    if (typeof option === 'string') return isObjectId(option) ? null : { _id: '', value: option, selected: false };
    const value = option?.value ?? option?.label ?? option?.name ?? '';
    if (!value || isObjectId(value)) return null;
    const vid = String(option?._id ?? option?.id ?? '');
    return { _id: vid, value, selected: vid ? selectedSet.has(vid) : false };
  };
  const lists: MemberAttributeGroupState[] = [];
  if (Array.isArray(rawAttributes)) {
    rawAttributes.forEach((group: any) => {
      if (!group || (typeof group === 'string' && isObjectId(group))) return;
      const groupName =
        typeof group === 'string'
          ? group
          : group?.name ?? group?.title ?? group?.label ?? group?.attributeName ?? group?.key;
      const gid = typeof group === 'string' ? '' : String(group?._id ?? group?.id ?? '');
      const raw =
        typeof group === 'string'
          ? []
          : group?.values ?? group?.options ?? group?.attributes ?? group?.items ?? group?.list ?? [];
      const values = (
        Array.isArray(raw) ? raw.map(toValue).filter(Boolean) : []
      ) as MemberAttributeGroupState['values'];
      if (groupName && !isCorruptedName(String(groupName))) lists.push({ _id: gid, name: String(groupName), values });
    });
  } else if (rawAttributes && typeof rawAttributes === 'object') {
    Object.entries(rawAttributes).forEach(([name, options]) => {
      if (isCorruptedName(name)) return;
      const values = (
        Array.isArray(options) ? options.map(toValue).filter(Boolean) : []
      ) as MemberAttributeGroupState['values'];
      lists.push({ _id: '', name, values });
    });
  }
  return lists;
};

// Which list is Department: the Teams list, else a list named Department.
const findDepartmentIndex = (lists: MemberAttributeGroupState[], teamsListId: string | null) => {
  if (teamsListId) {
    const flagged = lists.findIndex((l) => l._id === teamsListId);
    if (flagged >= 0) return flagged;
  }
  return lists.findIndex((l) => isDepartmentName(l.name));
};

// Department first (an empty one when the org has none yet), then the rest.
const withDepartmentFirst = (lists: MemberAttributeGroupState[], teamsListId: string | null) => {
  const i = findDepartmentIndex(lists, teamsListId);
  if (i < 0) return [{ _id: '', name: DEPARTMENT, values: [] }, ...lists];
  return [lists[i], ...lists.filter((_l, idx) => idx !== i)];
};

// Department first again once the Teams list is known, dropping the empty
// stand-in Department.
const putTeamsListFirst = (lists: MemberAttributeGroupState[], teamsListId: string) =>
  withDepartmentFirst(
    lists.filter((l, i) => !(i === 0 && !l._id && l.values.length === 0)),
    teamsListId
  );

const chosen = (list?: MemberAttributeGroupState) => (list?.values || []).filter((v) => v.selected !== false);

// The PUT body, minus lists the admin started but left empty (an unsaved
// Department with no departments yet, a new list with no values).
const toPayload = (lists: MemberAttributeGroupState[]) =>
  buildGroupPutPayload(lists.filter((l) => l._id || chosen(l).length > 0));

const Divider = () => <Box sx={{ height: '1px', bgcolor: '#2A2A2A', flexShrink: 0 }} />;

// A section of the card: title (+ extras on the right), helper copy, content.
const Section: React.FC<{
  title: React.ReactNode;
  helper?: React.ReactNode;
  aside?: React.ReactNode;
  testId?: string;
  children: React.ReactNode;
}> = ({ title, helper, aside, testId, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }} data-testid={testId}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        // A phone: what sits beside the title drops under it, rather than squeeze it.
        [MOBILE_SX]: { flexWrap: 'wrap', rowGap: '8px' }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        {title}
        {helper && <Typography sx={helperTextSx}>{helper}</Typography>}
      </Box>
      {aside}
    </Box>
    {children}
  </Box>
);

const rowGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: `${NAME_COLUMN}px minmax(0, 1fr)` },
  columnGap: '16px',
  rowGap: '8px'
};

// An inline "name it" form: text field + Cancel + the add button.
const NameForm: React.FC<{
  placeholder: string;
  addLabel: string;
  onAdd: (name: string) => boolean;
  onCancel: () => void;
  testId?: string;
}> = ({ placeholder, addLabel, onAdd, onCancel, testId }) => {
  const [name, setName] = useState('');
  const submit = () => {
    if (onAdd(name.trim())) setName('');
  };
  return (
    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }} data-testid={testId}>
      <TextField
        size="small"
        autoFocus
        placeholder={placeholder}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onCancel();
        }}
        sx={{ ...inputSx, flex: 1 }}
      />
      <DosenButton sizeVariant="sm" tone="basic" onClick={onCancel}>
        Cancel
      </DosenButton>
      <DosenButton sizeVariant="sm" tone="cta" disabled={!name.trim()} onClick={submit}>
        {addLabel}
      </DosenButton>
    </Box>
  );
};

const NewTag = () => (
  <Box
    component="span"
    sx={{
      px: '6px',
      py: '1px',
      borderRadius: '4px',
      bgcolor: 'rgba(247,148,32,0.12)',
      color: '#F79420',
      fontSize: 11,
      fontFamily: 'Inter',
      fontWeight: 600
    }}
  >
    New
  </Box>
);

const AudienceAttributesSection: React.FC<Props> = ({ data, grpId }) => {
  const dispatch = useAppDispatch();
  const capabilityRelease = useCapabilityRelease();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState<MemberAttributeGroupState[]>([]);
  const [savedPayload, setSavedPayload] = useState('[]');
  const [savedLists, setSavedLists] = useState<MemberAttributeGroupState[]>([]);
  const initialGrpMemberValueIdsRef = useRef<Set<string>>(new Set());
  const [addingDepartment, setAddingDepartment] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [departmentError, setDepartmentError] = useState(false);

  // The org's Teams list (Department), from the org; the managers GET says
  // which list the BE takes once it answers.
  const orgTeamsListId = useMemo(() => resolveTeamsListId(data?.memberAttributes), [data?.memberAttributes]);

  // Department's saved values: the teams the BE knows, with their managers.
  const savedDepartmentKey = (savedLists[0]?.values || [])
    .filter((v) => v._id)
    .map((v) => v._id)
    .join('|');
  const managers = useTeamManagers(data?.authId, savedDepartmentKey);
  const teamsListId = managers.teamsList !== undefined ? managers.teamsList?.listId ?? null : orgTeamsListId;
  const teamsListIdRef = useRef(teamsListId);
  teamsListIdRef.current = teamsListId;

  // GET the lists, with Department as the BE holds it.
  const fetchLists = async (listId: string | null) => {
    const response = await API.getGroupMemberAttributes({ grpId });
    const payload = unwrapEnvelope(response) as Record<string, unknown>;
    // No fallback to profileOrgMemberAttributes here: that value is the
    // LOGGED-IN admin's own org catalog, so for a platform admin managing
    // another org it leaked the wrong org's lists into this card.
    const { orgCatalog, grpIds } = parseGroupMemberAttributesResponse({ data: payload } as unknown, undefined);
    let next = mergeGrpSelection(orgCatalog, grpIds);
    if (!next.length) {
      const grpSelectedIds = Array.isArray((payload as { grpMemberAttributes?: string[] }).grpMemberAttributes)
        ? (payload as { grpMemberAttributes: string[] }).grpMemberAttributes.map(String)
        : [];
      next = normalizeAttributeLists(payload, grpSelectedIds);
    }
    const department = rawDepartmentList(payload, listId, grpIds);
    if (department) next = [department, ...next.filter((l) => l._id !== department._id)];
    return { lists: next, orgCatalog, grpIds };
  };

  const applyLoaded = (loaded: MemberAttributeGroupState[], grpIds: string[], listId: string | null) => {
    const ordered = withDepartmentFirst(loaded, listId);
    setLists(ordered);
    setSavedLists(ordered);
    setSavedPayload(JSON.stringify(toPayload(ordered)));
    initialGrpMemberValueIdsRef.current = new Set(grpIds.filter(isMongoObjectIdString));
  };

  useEffect(() => {
    if (!grpId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    fetchLists(teamsListIdRef.current)
      .then(({ lists: loaded, grpIds }) => alive && applyLoaded(loaded, grpIds, teamsListIdRef.current))
      .catch(
        (e) =>
          alive &&
          toast.error(
            _.get(e, 'response.data.message', 'Attributes could not be loaded. Refresh the page and try again.')
          )
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grpId]);

  // The managers GET can name a Teams list the org data didn't (an older org
  // payload): reload so it comes first, with every value.
  const loadedWithRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (loading || !grpId || !teamsListId || lists[0]?._id === teamsListId) return undefined;
    if (loadedWithRef.current === teamsListId) return undefined;
    loadedWithRef.current = teamsListId;
    let alive = true;
    fetchLists(teamsListId)
      .then(({ lists: loaded, grpIds }) => {
        if (!alive) return;
        const ordered = putTeamsListFirst(loaded, teamsListId);
        setSavedLists(ordered);
        setSavedPayload(JSON.stringify(toPayload(ordered)));
        setLists(ordered);
        initialGrpMemberValueIdsRef.current = new Set(grpIds.filter(isMongoObjectIdString));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsListId, loading, grpId]);

  const [department, ...otherLists] = lists;
  // Department's rows: every saved department, any team the BE has that this
  // list doesn't show, then the ones added since the last save.
  const departmentRows = useMemo(() => {
    const saved = (department?.values || []).filter((v) => v._id).map((v) => ({ _id: v._id, value: v.value }));
    const seen = new Set(saved.map((v) => v._id));
    const fromTeams =
      department?._id && department._id === teamsListId
        ? managers.teams.filter((t) => !seen.has(t.id)).map((t) => ({ _id: t.id, value: t.name }))
        : [];
    const added = chosen(department)
      .filter((v) => !v._id)
      .map((v) => ({ _id: '', value: v.value }));
    return [...saved, ...fromTeams, ...added];
  }, [department, managers.teams, teamsListId]);
  const savedDepartments = departmentRows.filter((v) => v._id).map((v) => ({ id: v._id, name: v.value }));
  // Sections are numbered: 1 Departments, 2 Roles (with the Capability
  // release), then each other list.
  const firstListNumber = capabilityRelease ? 3 : 2;
  const listsDirty = !loading && JSON.stringify(toPayload(lists)) !== savedPayload;
  // Picking managers counts as a change of this card too.
  const dirty = listsDirty || managers.dirty;
  // Saved departments on show whose manager box is empty.
  const missingManagers = managers.loading
    ? 0
    : departmentRows.filter((v) => {
        const team = v._id ? managers.teams.find((t) => t.id === v._id) : undefined;
        return Boolean(team && managers.needsManager(team));
      }).length;

  // Set which values of a list are chosen. A value the server doesn't have yet
  // is dropped when unchosen; a saved one stays in the catalog, unchosen.
  const setListValues = (index: number, nextValues: string[]) => {
    if (index === 0) setDepartmentError(false);
    setLists((prev) => {
      const list = prev[index];
      if (!list) return prev;
      const picked = new Set(nextValues.map((v) => v.trim()).filter(Boolean));
      const existing = list.values
        .filter((item) => item._id || picked.has(item.value))
        .map((item) => ({ ...item, selected: picked.has(item.value) }));
      const additions = Array.from(picked)
        .filter((val) => !list.values.some((item) => item.value === val))
        .map((val) => ({ _id: '', value: val, selected: true }));
      const next = [...prev];
      next[index] = { ...list, values: [...existing, ...additions] };
      return next;
    });
  };

  const addDepartment = (name: string) => {
    if (!name) return false;
    if (departmentRows.some((v) => v.value.toLowerCase() === name.toLowerCase())) {
      toast.error(`${name} is already a department.`);
      return false;
    }
    setListValues(0, [...chosen(department).map((v) => v.value), name]);
    return true;
  };

  const removeNewDepartment = (name: string) =>
    setListValues(
      0,
      chosen(department)
        .map((v) => v.value)
        .filter((v) => v !== name)
    );

  const createList = (name: string) => {
    if (!name) return false;
    if (lists.some((l) => l.name.trim().toLowerCase() === name.toLowerCase())) {
      toast.error(`There is already a list called ${name}.`);
      return false;
    }
    setLists((prev) => [...prev, { _id: '', name, values: [] }]);
    setCreatingList(false);
    return true;
  };

  const removeNewList = (index: number) => setLists((prev) => prev.filter((_l, i) => i !== index));

  // A saved Department that isn't the Teams list yet (an org that named a list
  // Department before this card) gets its managers once it is marked as one.
  const [markingTeamsList, setMarkingTeamsList] = useState(false);
  const markDepartmentAsTeamsList = async () => {
    if (!department?._id || !data?.authId) return;
    setMarkingTeamsList(true);
    try {
      const res: any = await API.updateOrgTeamsList({
        orgId: data.authId,
        listId: department._id,
        ...getLLAuthHeaders()
      });
      const memberAttributes = res?.data?.data?.memberAttributes ?? res?.data?.memberAttributes;
      if (Array.isArray(memberAttributes)) dispatch(orgSettingsActions.updateOrgMemberAttributes(memberAttributes));
      // Reload the managers now that the BE knows the teams.
      managers.reload();
    } catch (e) {
      toast.error(_.get(e, 'response.data.message', 'Department could not be set up. Try again.'));
    } finally {
      setMarkingTeamsList(false);
    }
  };

  const handleCancel = () => {
    setLists(savedLists);
    managers.cancel();
    setDepartmentError(false);
    setAddingDepartment(false);
    setCreatingList(false);
  };

  const handleSave = async () => {
    if (!grpId) {
      toast.error('Group ID not found for this organization.');
      return;
    }
    if (listsDirty && departmentRows.length === 0) {
      setDepartmentError(true);
      toast.error('Add at least one department.');
      return;
    }
    // Only a Department this save creates becomes the Teams list: an existing
    // list is marked with "Set up managers", never as a side effect.
    const createsDepartment = !department?._id && !teamsListId;
    setSaving(true);
    try {
      // Managers first: saving the lists reloads the departments, and the
      // managers with them.
      if (managers.dirty) {
        const saved = await managers.saveAll();
        if (saved && !listsDirty) toast.success('Managers saved.');
      }
      if (!listsDirty) return;
      const attemptedRemoval = detectAttemptedGrpDeselection(initialGrpMemberValueIdsRef.current, lists);
      const putRes = await API.updateGroupMemberAttributes({ grpId, data: toPayload(lists) });
      const added = (putRes as any)?.data?.data?.added ?? (putRes as any)?.data?.added ?? (putRes as any)?.added;
      const reloaded = await fetchLists(teamsListId);
      dispatch(setOrgMemberAttributes(reloaded.orgCatalog));
      const createdId = createsDepartment
        ? reloaded.lists.find((l) => l._id && isDepartmentName(l.name))?._id || null
        : null;
      applyLoaded(reloaded.lists, reloaded.grpIds, createdId || teamsListId);
      setAddingDepartment(false);

      // The Department this save created becomes the org's Teams list. The
      // lists are saved either way, so a failure here says only that.
      if (createdId) {
        try {
          const res: any = await API.updateOrgTeamsList({
            orgId: data.authId,
            listId: createdId,
            ...getLLAuthHeaders()
          });
          const memberAttributes = res?.data?.data?.memberAttributes ?? res?.data?.memberAttributes;
          if (Array.isArray(memberAttributes)) dispatch(orgSettingsActions.updateOrgMemberAttributes(memberAttributes));
          managers.reload();
        } catch {
          toast.error('Departments saved, but their managers could not be set up. Use Set up managers to try again.');
        }
      }

      let msg = typeof added === 'number' ? `${added} attributes added successfully` : 'Audience attributes saved.';
      if (attemptedRemoval) {
        // This card edits the ORG-LEVEL attribute catalog against the org's
        // root group, whose membership is filtered out everywhere "your
        // programs" is computed — so deselecting here never adds or removes
        // anyone from a program. Audience membership (including removal) is
        // managed per program on the Program Card.
        msg +=
          ' Note: this only updates the organization’s attribute catalog. To change who is enrolled in a program, edit that program’s Audience.';
      }
      // Saving the catalog flags every group member (this admin included) with
      // memberAttributesChanged. Mark that this session's user just authored
      // the attributes so the Welcome back prompt does not bounce them into
      // the mandatory "Welcome Back" prompt while they are still setting up.
      try {
        sessionStorage.setItem(ATTR_AUTHORED_KEY, 'true');
      } catch {
        /* sessionStorage can be unavailable (private mode); never block save */
      }
      toast.success(msg);
    } catch (e) {
      toast.error(_.get(e, 'response.data.message', 'Attributes could not be saved. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const renderDepartments = () => {
    const rows = departmentRows;
    return (
      <Section
        testId="department-list"
        title={<Typography sx={sectionTitleSx}>1. Departments or Teams *</Typography>}
        helper="Learners will be enrolled and capabilities will be assessed by department so please add here now."
        aside={
          missingManagers > 0 ? (
            <Typography sx={{ ...helperTextSx, color: '#F79420', fontWeight: 600, whiteSpace: 'nowrap', pt: '2px' }}>
              {missingManagers === 1 ? '1 department needs a manager' : `${missingManagers} departments need a manager`}
            </Typography>
          ) : undefined
        }
      >
        <Box data-testid="department-managers">
          {rows.length > 0 && (
            <Box sx={{ ...rowGridSx, display: { xs: 'none', md: 'grid' }, pb: '8px' }}>
              <Typography sx={columnHeadingSx}>Department</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={columnHeadingSx}>Managers *</Typography>
                <Tooltip title="Org admins manage every department automatically, so they aren't listed here.">
                  <InfoOutlinedIcon sx={{ color: '#868686', fontSize: 13 }} />
                </Tooltip>
              </Box>
            </Box>
          )}
          {rows.map((value) => {
            const team = value._id ? managers.teams.find((t) => t.id === value._id) : undefined;
            const required = Boolean(team && managers.needsManager(team));
            return (
              <Box
                key={value._id || `new-${value.value}`}
                data-testid={`department-manager-row-${value.value}`}
                sx={{ ...rowGridSx, py: '16px', borderTop: '1px solid #2A2A2A', alignItems: 'start' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', pt: { md: '8px' }, minWidth: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          color: '#FDFDFD',
                          wordBreak: 'break-word'
                        }}
                      >
                        {value.value}
                      </Typography>
                      {!value._id && <NewTag />}
                    </Box>
                    {required && (
                      <Typography sx={{ ...helperTextSx, color: '#F79420', mt: '2px' }}>Manager required</Typography>
                    )}
                  </Box>
                  {!value._id && (
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        aria-label={`Remove ${value.value}`}
                        onClick={() => removeNewDepartment(value.value)}
                        sx={{ color: '#868686', mt: '-4px', '&:hover': { color: '#FDFDFD' } }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {!value._id ? (
                  <Box
                    sx={{
                      minHeight: 42,
                      px: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px dashed #333333',
                      borderRadius: '8px'
                    }}
                  >
                    <Typography sx={{ ...helperTextSx, color: '#868686' }}>
                      Save Changes to assign this department&apos;s managers
                    </Typography>
                  </Box>
                ) : team ? (
                  <ManagerPicker team={team} managers={managers} />
                ) : (
                  <Box sx={{ minHeight: 42, display: 'flex', alignItems: 'center' }}>
                    {managers.loading ? (
                      <Typography sx={helperTextSx}>Loading managers...</Typography>
                    ) : !teamsListId ? (
                      <DosenButton
                        sizeVariant="xs"
                        tone="options"
                        loading={markingTeamsList}
                        loadingText="Setting up..."
                        onClick={markDepartmentAsTeamsList}
                      >
                        Set up managers
                      </DosenButton>
                    ) : (
                      <Typography sx={helperTextSx}>Managers could not be loaded.</Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
          <Box sx={{ pt: rows.length ? '12px' : 0, borderTop: rows.length ? '1px solid #2A2A2A' : 'none' }}>
            {addingDepartment ? (
              <NameForm
                testId="add-department-form"
                placeholder="Department name, e.g. Sales"
                addLabel="Add department"
                onAdd={addDepartment}
                onCancel={() => setAddingDepartment(false)}
              />
            ) : (
              <Box component="button" type="button" onClick={() => setAddingDepartment(true)} sx={addRowButtonSx}>
                + Add department
              </Box>
            )}
          </Box>
          {departmentError && (
            <Typography sx={{ ...helperTextSx, color: '#F79420', mt: '8px' }}>Add at least one department.</Typography>
          )}
        </Box>
      </Section>
    );
  };

  return (
    <Box sx={cardSx} data-testid="audience-attributes-card">
      <Box sx={cardHeaderSx}>
        <Typography sx={cardTitleSx}>Audience attributes</Typography>
        <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: 400 }}>
          (for Automated Program Enrollment)
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: '10px' }}>
          {dirty && (
            <DosenButton sizeVariant="sm" tone="basic" disabled={saving} onClick={handleCancel}>
              Cancel Changes
            </DosenButton>
          )}
          <DosenButton
            sizeVariant="sm"
            tone="cta"
            loading={saving}
            loadingText="Saving..."
            disabled={!dirty}
            onClick={handleSave}
          >
            Save Changes
          </DosenButton>
        </Box>
      </Box>

      <Box
        sx={{
          px: '25px',
          py: '22px',
          display: 'flex',
          flexDirection: 'column',
          gap: '22px',
          [MOBILE_SX]: { px: '16px' }
        }}
      >
        {loading ? (
          <Typography sx={helperTextSx}>Loading attributes...</Typography>
        ) : !grpId ? (
          <Typography sx={helperTextSx}>Group ID not found for this organization.</Typography>
        ) : (
          <>
            {renderDepartments()}

            {capabilityRelease && (
              <>
                <Divider />
                <TeamRolesSection number={2} teamValues={savedDepartments} />
              </>
            )}

            <Divider />

            {/* Every other list: a numbered row like a department row — its
                name on the left, its values on the right. */}
            <Box data-testid="attribute-lists">
              {otherLists.map((list, i) => {
                const index = i + 1;
                const number = firstListNumber + i;
                return (
                  <Box
                    key={list._id || `new-${list.name}`}
                    data-testid={`attribute-list-${list.name}`}
                    sx={{
                      ...rowGridSx,
                      py: '16px',
                      borderTop: i > 0 ? '1px solid #2A2A2A' : 'none',
                      pt: i > 0 ? '16px' : 0,
                      alignItems: 'start'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', pt: { md: '8px' }, minWidth: 0 }}>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}
                      >
                        <Typography sx={{ ...sectionTitleSx, wordBreak: 'break-word' }}>
                          {number}. {list.name}
                        </Typography>
                        {!list._id && <NewTag />}
                      </Box>
                      {!list._id && (
                        <Tooltip title="Remove list">
                          <IconButton
                            size="small"
                            aria-label={`Remove ${list.name}`}
                            onClick={() => removeNewList(index)}
                            sx={{ color: '#868686', mt: '-4px', '&:hover': { color: '#FDFDFD' } }}
                          >
                            <CloseRoundedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <ValuesInput
                      options={list.values.map((v) => v.value)}
                      value={chosen(list).map((v) => v.value)}
                      placeholder="Type a value and press Enter"
                      onChange={(next) => setListValues(index, next)}
                      testId={`attribute-input-${list.name}`}
                    />
                  </Box>
                );
              })}

              <Box
                sx={{
                  pt: otherLists.length ? '12px' : 0,
                  borderTop: otherLists.length ? '1px solid #2A2A2A' : 'none'
                }}
              >
                {creatingList ? (
                  <Box sx={{ ...rowGridSx, alignItems: 'center' }} data-testid="create-attribute-list">
                    <Typography sx={sectionTitleSx}>
                      {firstListNumber + otherLists.length}. New attribute list
                    </Typography>
                    <NameForm
                      placeholder="List name, e.g. Location"
                      addLabel="Add list"
                      onAdd={createList}
                      onCancel={() => setCreatingList(false)}
                    />
                  </Box>
                ) : (
                  <Box component="button" type="button" onClick={() => setCreatingList(true)} sx={addRowButtonSx}>
                    + Add attribute list
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AudienceAttributesSection;
